import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Network, ShieldCheck, Activity as ActivityIcon, FileSignature, LayoutDashboard, Star, BarChart3, TrendingUp } from "lucide-react";
import PlatformOrganogram from "@/pages/PlatformOrganogram";
import Approvals from "@/pages/Approvals";
import Activity from "@/pages/Activity";
import MyPolicies from "@/pages/MyPolicies";
import Reports from "@/pages/Reports";
import IncreasePage from "@/pages/IncreasePage";
import GlobalDashboard, { GlobalSkillsMatrix } from "@/pages/GlobalDashboard";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, Component: GlobalDashboard, testId: "global-tab-dashboard" },
  { id: "reports", label: "Reports & Analytics", icon: BarChart3, Component: Reports, testId: "global-tab-reports" },
  { id: "increase", label: "Increase", icon: TrendingUp, Component: IncreasePage, testId: "global-tab-increase" },
  { id: "skills", label: "Skills Matrix", icon: Star, Component: GlobalSkillsMatrix, testId: "global-tab-skills" },
  { id: "organogram", label: "All BUs Organogram", icon: Network, Component: PlatformOrganogram, testId: "global-tab-organogram" },
  { id: "approvals", label: "Approvals", icon: ShieldCheck, Component: Approvals, testId: "global-tab-approvals" },
  { id: "audit", label: "Audit Log", icon: ActivityIcon, Component: Activity, testId: "global-tab-audit" },
  { id: "my-policies", label: "My Policies", icon: FileSignature, Component: MyPolicies, testId: "global-tab-policies" },
];

export default function Global() {
  const [params, setParams] = useSearchParams();
  const initial = TABS.find((t) => t.id === params.get("tab"))?.id || "dashboard";
  const [tab, setTab] = useState(initial);

  // Keep URL query param in sync so tiles that link to /global?tab=xyz work.
  useEffect(() => {
    if (tab !== params.get("tab")) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, [tab]);

  // Respond to external navigations (e.g. dashboard tile → /global?tab=skills)
  useEffect(() => {
    const t = params.get("tab");
    if (t && t !== tab && TABS.some((x) => x.id === t)) setTab(t);
    // eslint-disable-next-line
  }, [params]);

  const Active = TABS.find((t) => t.id === tab)?.Component || GlobalDashboard;
  return (
    <div data-testid="global-workspace">
      <div className="mb-2 flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-sm flex items-center justify-center text-white"
          style={{ background: "var(--brand-primary, #2C7BB6)" }}
        >
          <Network className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Global</h1>
          <div className="text-xs text-zinc-500 mt-1">Platform-wide workspace for Super Admins.</div>
        </div>
      </div>
      <div className="border-b border-zinc-200 mt-6 mb-6 flex gap-6 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className={`pb-3 text-sm border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                tab === t.id ? "border-zinc-900 text-zinc-900 font-semibold" : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} /> {t.label}
            </button>
          );
        })}
      </div>
      <Active />
    </div>
  );
}
